import React, { useEffect, useState } from 'react';
import styled from 'styled-components';
import Loader from '../Components/Loader';
import SuccessCard from '../Components/SuccessCard';
import ErrorCard from '../Components/ErrorCard';
import useIAList from '../Hooks/NisHooks/useIAList';
import useSystemNameList from '../Hooks/NisMasterlistHooks/useSystemNameList';
import useAddNisMasterlist from '../Hooks/NisMasterlistHooks/useAddNisMasterlist';
import useUpdateNisMasterlist from '../Hooks/NisMasterlistHooks/useUpdateNisMasterlist';
import MunicipalitySelector from '../Components/Municipality';
import MailingAddressSelector from '../Components/MailingAddressSelector';

const NisMasterlistAdd = ({ darkMode, onClose, selectedItem, mode, onAddSuccess, year, presetIaName, presetIsName }) => {
  const isEdit = mode === 'edit' && selectedItem && selectedItem.id;
  const lockedSystemName = presetIsName || '';
  const { loading, success, error, addNisMasterlist } = useAddNisMasterlist();
  const {
    loading: updateLoading,
    success: updateSuccess,
    error: updateError,
    updateNisMasterlist,
  } = useUpdateNisMasterlist();
  const { iaList, loading: iaLoading, error: iaError } = useIAList();
  const { systemNameList, loading: isLoading, error: isError } = useSystemNameList();
  const safeSystemNameList = Array.isArray(systemNameList) ? systemNameList : [];
  const [showSuccess, setShowSuccess] = useState(false);
  const [showError, setShowError] = useState(false);
  const [validationErrors, setValidationErrors] = useState({});

  const initialFormData = {
    nis_id: '',
    years: '',
    system_name: '',
    system_type: '',
    ia_name: '',
    ia_region: '',
    ia_province: '',
    ia_municipality: '',
    ia_barangay: '',
    lastname: '',
    firstname: '',
    middlename: '',
    extensionName: '',
    sex: '',
    birthday: '',
    mothers_maidenName: '',
    spouse_lastname: '',
    spouse_firstName: '',
    spouse_middleName: '',
    spouse_extName: '',
    region: '',
    province: '',
    municipality: '',
    barangay: '',
    houseNumber: '',
    regionFarm: '',
    provinceFarm: '',
    municipalityFarm: '',
    barangayFarm: '',
    farm_lotNum: '',
    farm_lotCode: '',
    farmArea: '',
    ownershipType: '',
    rsbsa_registered: '',
    da_imc: '',
    rsbsa_refNum: '',
    rsbsa_controlNum: '',
    national_id: '',
    philsys_cardNum: '',
    transactionSlip: '',
    custom_system_name: '',
  };
  const [formData, setFormData] = useState(initialFormData);
  const [isExpanded] = useState(true);

  // Pre-fill form when editing an existing record
  useEffect(() => {
    if (isEdit) {
      const next = { ...initialFormData };
      Object.keys(initialFormData).forEach((k) => {
        if (selectedItem[k] !== undefined && selectedItem[k] !== null) {
          next[k] = selectedItem[k];
        }
      });
      // Map DB column is_name -> form field system_name when system_name not present
      if ((!next.system_name || next.system_name === '') && selectedItem.is_name) {
        next.system_name = selectedItem.is_name;
      }
      setFormData(next);
    } else if (presetIaName || presetIsName) {
      // When adding a new row from a NIS-scoped masterlist view, pre-fill the IA / IS
      setFormData(prev => ({
        ...prev,
        ia_name: presetIaName || prev.ia_name,
        system_name: presetIsName || prev.system_name,
      }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isEdit, selectedItem?.id, presetIaName, presetIsName]);

  const formSections = [
    {
      title: null,
      fields: [
        'system_name',
        'system_type',
      ],
    },
    {
      title: null,
      fields: [
        'ia_name',
        'ia_region',
        'ia_province',
        'ia_municipality',
        'ia_barangay',
      ],
    },
     {
      title: null,
      fields: [
        'lastname',
        'firstname',
        'middlename',
        'extensionName',
        'sex',
        'birthday',
        'mothers_maidenName',
      ],
    },
    {
      title: null,
      fields: [
        'spouse_lastname',
        'spouse_firstName',
        'spouse_middleName',
        'spouse_extName',
      ],
    },
   {
      title: null,
      fields: [
        'region',
        'province',
        'municipality',
        'barangay',
        'houseNumber',
      ],
    },
    {
      title: null,
      fields: [
        'regionFarm',
        'provinceFarm',
        'municipalityFarm',
        'barangayFarm',
        'farm_lotNum',
        'farmArea',
        'ownershipType',
        'rsbsa_registered',
        'da_imc',
        'rsbsa_refNum',
        'rsbsa_controlNum',
        'national_id',
        'philsys_cardNum',
        'transactionSlip',
      ],
    },
  ];

  const fieldLabelMap = {
    system_name: 'Name of System',
    system_type: 'Type of System',
    ia_name: "Name of IA",
    ia_region: 'Region ia',
    ia_province: 'Province ia',
    ia_municipality: 'Municipality ia',
    ia_barangay: 'Barangay ia',
    lastname: 'Last Name',
    firstname: 'First Name',
    middlename: 'Middle Name',
    extensionName: 'Extension Name',
    sex: 'sex (M/F)',
    birthday: "Birthday",
    mothers_maidenName: 'Mothers Maiden Name',
    spouse_lastname: 'Spouse Lastname',
    spouse_firstName: 'Spouse Firstname',
    spouse_middleName: 'Spouse Middlename',
    spouse_extName: 'Spouse ext Name',
    region: 'Region',
    province: 'Province',
    municipality: 'Municipality',
    barangay: 'Barangay',
    houseNumber: 'House Number/Street/Purok',
    regionFarm: 'Regionfarm',
    provinceFarm: 'Provincefarm',
    municipalityFarm: 'Municipaliityfarm',
    barangayFarm: 'Barangayfarm',
    farm_lotNum: 'Farm Lot No.',
    farmArea: 'Farm Area (Ha)',
    ownershipType: 'Type of Ownership',
    rsbsa_registered: 'RSBSA Registered? (Y/N)',
    da_imc: 'With DA IMC? (Y/N)',
    rsbsa_refNum: 'IF YES, RSBSA REFERENCE NO.? (00-00-00-000-000000)',
    rsbsa_controlNum: 'IF NONE, RSBSA STUB CONTROL NUMBER? (00-00-00-000-000000)',
    national_id: 'WITH NATIONAL ID? (Y/N)',
    philsys_cardNum: 'IF YES, PHILSYS CARD NUMBER? 0000-0000-0000-0000',
    transactionSlip: 'IF NONE, TRANSACTION SLIP NO. or type LOST, if Transaction Slip was lost ',
    custom_system_name: 'Custom  Name of System',
  };

const requiredFields = isEdit
  ? ['system_name', 'ia_name', 'lastname', 'firstname']
  : ['system_name', 'ia_name', 'lastname', 'firstname'];

const validateForm = () => {
  const errors = {};

  requiredFields.forEach((field) => {
    const v = formData[field];
    if (v === undefined || v === null || (typeof v === 'string' && v.trim() === '')) {
      errors[field] = true;
    }
  });

  setValidationErrors(errors);
  return Object.keys(errors).length === 0;
};



const handleChange = (e) => {
  const { name, value } = e.target;

  if (!isEdit && presetIsName && name === 'system_name') {
    return;
  }

  // Automatically set nis_id when selecting system_name
  if (name === 'system_name' && value !== 'Others ...') {
    const selectedSystem = safeSystemNameList.find(sn => sn.system_name === value);
    setFormData(prev => ({
      ...prev,
      system_name: value,
      nis_id: selectedSystem?.id ? Number(selectedSystem.id) : null,
    }));
  } else {
    setFormData(prev => ({
      ...prev,
      [name]: value,
    }));
  }
};


  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateForm()) {
      setShowError(true);
      return;
    }
    const submissionData = {
      ...formData,
      system_name: formData.system_name === 'Others ...' ? formData.custom_system_name.toUpperCase() : formData.system_name,
      ia_name: formData.ia_name === 'Others ...' ? formData.custom_ia_name.toUpperCase() : formData.ia_name,
      
       nis_id: formData.nis_id,
    };
    
const cleanedData = Object.fromEntries(
  Object.entries(formData).map(([key, value]) => [key, value === undefined ? null : value])
);


const requiredPayload = {
  years: formData.years || (selectedItem && selectedItem.years) || year || new Date().getFullYear(),
  is_name: lockedSystemName || (formData.system_name === 'Others ...' ? formData.custom_system_name : formData.system_name),
  system_name: lockedSystemName || (formData.system_name === 'Others ...' ? formData.custom_system_name : formData.system_name),
  nis_id: formData.nis_id,
  system_type: formData.system_type,
  ia_name: formData.ia_name === 'Others ...' ? formData.custom_ia_name : formData.ia_name,
  ia_region: formData.ia_region,
  ia_province: formData.ia_province,
  ia_municipality: formData.ia_municipality,
  ia_barangay: formData.ia_barangay,
  lastname: formData.lastname,
  firstname: formData.firstname,
  middlename: formData.middlename,
  extensionName: formData.extensionName,
  sex: formData.sex,
  birthday: formData.birthday,
  mothers_maidenName: formData.mothers_maidenName,
  spouse_lastname: formData.spouse_lastname,
  spouse_firstName: formData.spouse_firstName,
  spouse_middleName: formData.spouse_middleName,
  spouse_extName: formData.spouse_extName,
  region: formData.region,
  province: formData.province,
  municipality: formData.municipality,
  barangay: formData.barangay,
  houseNumber: formData.houseNumber,
  regionFarm: formData.regionFarm,
  provinceFarm: formData.provinceFarm,
  municipalityFarm: formData.municipalityFarm,
  barangayFarm: formData.barangayFarm,
  farm_lotNum: formData.farm_lotNum,
  farm_lotCode: formData.farm_lotCode,
  farmArea: parseFloat(formData.farmArea || 0),
  ownershipType: formData.ownershipType,
  rsbsa_registered: formData.rsbsa_registered,
  da_imc: formData.da_imc,
  rsbsa_refNum: formData.rsbsa_refNum,
  rsbsa_controlNum: formData.rsbsa_controlNum,
  national_id: formData.national_id,
  philsys_cardNum: formData.philsys_cardNum,
  transactionSlip: formData.transactionSlip
};
console.log('🚀 Submitting this to backend:', requiredPayload);

const result = isEdit
  ? await updateNisMasterlist(selectedItem.id, requiredPayload)
  : await addNisMasterlist(requiredPayload);


    if (result?.success || success || updateSuccess) {
      setShowSuccess(true);
      setShowError(false);
      if (!isEdit) {
        setFormData(initialFormData);
      }
      setValidationErrors({});
      if (typeof onAddSuccess === 'function') {
        onAddSuccess(isEdit ? 'NIS data successfully updated!' : 'NIS data successfully added!');
      }
    } else {
      setShowError(true);
      setShowSuccess(false);
    }
  };

  const handleClose = () => {
    setFormData(initialFormData);
    setValidationErrors({});
    setShowSuccess(false);
    setShowError(false);
    onClose();
  };

  return (
    <Container darkMode={darkMode} isExpanded={isExpanded}>
      <FormContainer darkMode={darkMode} isExpanded={isExpanded}>
        <Title darkMode={darkMode}>
          <span>{isEdit ? 'NIS Masterlist Edit' : 'NIS Masterlist Form'}</span>
          <CloseButton onClick={handleClose} darkMode={darkMode}>
            X
          </CloseButton>
        </Title>
        {(loading || updateLoading || iaLoading || isLoading) && (
          <LoaderOverlay>
            <Loader />
          </LoaderOverlay>
        )}
        {(error || updateError || iaError || isError) && (
          <ErrorCard
            errorMessage={error || updateError || iaError || isError || 'An error occurred while fetching data.'}
            onClose={() => setShowError(false)}
          />
        )}
        {showSuccess && (
          <SuccessCard
            message={isEdit ? 'NIS data successfully updated!' : 'NIS data successfully added!'}
            onClose={() => setShowSuccess(false)}
          />
        )}
        {showError && Object.keys(validationErrors).length > 0 && (
          <ErrorCard
            errorMessage="Please fill out all fields correctly."
            onClose={() => setShowError(false)}
          />
        )}

        <form onSubmit={handleSubmit}>
          {formSections.map((section, index) => (
            <CardSection darkMode={darkMode} key={index}>
              {section.title && (
                <SectionHeader darkMode={darkMode}>{section.title}</SectionHeader>
              )}
              {section.fields.map((key) => (
                <FieldGroup key={key} darkMode={darkMode} hasError={validationErrors[key]}>
                  <Label htmlFor={key} hasError={validationErrors[key]}>
                    {fieldLabelMap[key]}
                    <RequiredAsterisk>*</RequiredAsterisk>
                  </Label>
                  {key === 'years' ? (
                    <Input
                      type="text"
                      id={key}
                      name={key}
                      value={formData[key]}
                      onChange={handleChange}
                      placeholder="Enter Year (e.g., 2025)"
                      aria-label={fieldLabelMap[key]}
                      maxLength="4"
                      hasError={validationErrors[key]}
                    />
                  ) : key === 'system_name' ? (
                    <>
                      <Select
                        id={key}
                        name={key}
                        value={lockedSystemName || formData[key]}
                        onChange={handleChange}
                        aria-label={fieldLabelMap[key]}
                        hasError={validationErrors[key]}
                        disabled={Boolean(lockedSystemName) && !isEdit}
                      >
                        <option value="" disabled>Select Name of System</option>
                        {lockedSystemName && (
                          <option value={lockedSystemName}>
                            {lockedSystemName}
                          </option>
                        )}
                         <option value="Others ...">Add new </option>
                        {safeSystemNameList.map((sn) => (
                          <option key={sn.id || sn.system_name} value={sn.system_name}>
                            {sn.system_name}
                          </option>
                        ))}
                       
                      </Select>
                      {!lockedSystemName && formData.system_name === 'Others ...' && (
                        <Input
                          type="text"
                          id="custom_system_name"
                          name="custom_system_name"
                          value={formData.custom_system_name}
                          onChange={handleChange}
                          placeholder="Enter Custom IS Name"
                          aria-label={fieldLabelMap.custom_system_name}
                          hasError={validationErrors.custom_system_name}
                          style={{ marginTop: '8px' }}
                        />
                      )}
                    </>
                  ) : key === 'ia_name' ? (
                    <>
                      <Select
                        id={key}
                        name={key}
                        value={formData[key]}
                        onChange={handleChange}
                        aria-label={fieldLabelMap[key]}
                        hasError={validationErrors[key]}
                      >
                        <option value="" disabled>Select IA Name</option>
                        {iaList.map((ia) => (
                          <option key={ia.ia_id || ia.id} value={ia.ia_name}>
                            {ia.ia_name}
                          </option>
                        ))}
                        <option value="Others ...">Add new IA</option>
                      </Select>
                      {formData.ia_name === 'Others ...' && (
                        <Input
                          type="text"
                          id="custom_ia_name"
                          name="custom_ia_name"
                          value={formData.custom_ia_name}
                          onChange={handleChange}
                          placeholder="Enter Custom IA Name"
                          aria-label={fieldLabelMap.custom_ia_name}
                          hasError={validationErrors.custom_ia_name}
                          style={{ marginTop: '8px' }}
                        />
                      )}
                    </>
                  ) : key === 'municipality' ? (
                    <MunicipalitySelector
                      formData={formData}
                      setFormData={setFormData}
                      darkMode={darkMode}
                      hasError={validationErrors[key]}
                    />
        
                  ) : (
                    <Input
                      type="text"
                      id={key}
                      name={key}
                      value={formData[key]}
                      onChange={handleChange}
                      placeholder={
                        key === 'contactNum'
                          ? 'Enter Contact Number (7-15 digits)'
                          : key === 'ia_tin'
                          ? 'Enter TIN (alphanumeric, up to 20 chars)'
                          : fieldLabelMap[key]
                      }
                      aria-label={fieldLabelMap[key]}
                      maxLength={key === 'contactNum' ? '15' : key === 'ia_tin' ? '20' : '100'}
                      hasError={validationErrors[key]}
                    />
                  )}
                </FieldGroup>
              ))}
            </CardSection>
          ))}
          <FormButtonContainer>
            <SubmitButton darkMode={darkMode} type="submit">
              Add Information
            </SubmitButton>
          </FormButtonContainer>
        </form>
      </FormContainer>
    </Container>
  );
};

export default NisMasterlistAdd;

const Container = styled.div`
  padding: 20px;
  width: 100%;
  height: 100%;
  min-height: 100vh;
  background-size: cover;
  background-position: center;
  display: flex;
  flex-direction: column;
  justify-content: flex-start;
  overflow-y: auto;
  position: relative;
  transition: transform 0.3s ease;
  transform: ${({ isExpanded }) => (isExpanded ? 'translateX(0%)' : 'translateX(80%)')};
  background-color: ${({ darkMode }) => (darkMode ? 'transparent' : '#f2f2f2')};
  background-image: ${({ darkMode }) => (darkMode ? 'url(/path/to/adminBg.jpg)' : 'none')};
`;

const FormContainer = styled.div`
  flex: 1;
  width: 100%;
  max-width: 1200px;
  margin: 0 auto;
  padding: 5px;
  flex-grow: 1;
  overflow-y: auto;
  transition: width 0.3s ease, transform 0.3s ease;
  width: ${({ isExpanded }) => (isExpanded ? '100%' : 'calc(100% - 200px)')};

  @media (max-width: 1024px) {
    width: ${({ isExpanded }) => (isExpanded ? '100%' : 'calc(100% - 150px)')};
  }

  @media (max-width: 768px) {
    width: 100%;
    transform: translateX(0);
  }
`;

const Title = styled.h2`
  position: sticky;
  top: 0;
  z-index: 100;
  padding: 10px 5px;
  margin: 0 0 10px 0;
  font-size: 22px;
  font-weight: bold;
  color: ${({ darkMode }) => (darkMode ? '#ffffff' : '#333')};
  background: ${({ darkMode }) => (darkMode ? 'rgba(3, 82, 33, 0.45)' : '#f2f2f2')};
  border-bottom: 1px solid ${({ darkMode }) => (darkMode ? 'rgba(255, 255, 255, 0.1)' : '#ccc')};
  display: flex;
  justify-content: space-between;
  align-items: center;

  span {
    flex: 1;
    text-align: center;
  }
`;

const LoaderOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 9999;
  background: rgba(0, 0, 0, 0.5);
`;

const CardSection = styled.div`
  background: ${({ darkMode }) => (darkMode ? 'rgba(3, 82, 33, 0.3)' : '#fff')};
  backdrop-filter: ${({ darkMode }) => (darkMode ? 'blur(10px)' : 'none')};
  -webkit-backdrop-filter: ${({ darkMode }) => (darkMode ? 'blur(10px)' : 'none')};
  padding: 5px;
  margin-bottom: 10px;
  border-radius: 8px;
  box-shadow: ${({ darkMode }) => (darkMode ? '0 3px 8px rgba(255, 255, 255, 0.2)' : '0 3px 10px rgba(0, 0, 0, 0.1)')};
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 15px;

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
`;

const SectionHeader = styled.h3`
  grid-column: span 2;
  font-size: 14px;
  font-weight: bold;
  margin-bottom: 5px;
  color: ${({ darkMode }) => (darkMode ? '#fff' : '#333')};
  border-bottom: 1px solid ${({ darkMode }) => (darkMode ? '#ccc' : '#ddd')};
  padding-bottom: 5px;
`;

const FieldGroup = styled.div`
  display: flex;
  flex-direction: column;
`;

const Label = styled.label`
  font-weight: bold;
  font-size: 14px;
  margin-bottom: 5px;
  color: ${({ darkMode, hasError }) => (hasError ? '#ff0000' : darkMode ? '#f2f2f2' : '#333')};
`;

const RequiredAsterisk = styled.span`
  color: #ff0000;
  margin-left: 4px;
`;

const Input = styled.input`
  flex: 1;
  padding: 8px 12px;
  border: 1px solid ${({ hasError }) => (hasError ? '#ff0000' : '#4caf50')};
  border-radius: 4px;
  background-color: #ffffff;
  color: #333;
  font-size: 14px;
  width: 100%;
  transition: border-color 0.2s ease, background-color 0.2s ease;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;

  &:focus {
    outline: none;
    border-color: ${({ hasError }) => (hasError ? '#ff0000' : '#388e3c')};
  }

  &:hover {
    background-color: #f1f1f1;
  }

  &::placeholder {
    color: #888;
    font-style: italic;
    font-size: 14px;
  }
`;

const Select = styled.select`
  flex: 1;
  padding: 8px 12px;
  border: 1px solid ${({ hasError }) => (hasError ? '#ff0000' : '#4caf50')};
  border-radius: 4px;
  background-color: #ffffff;
  color: #333;
  font-size: 14px;
  width: 100%;
  transition: border-color 0.2s ease, background-color 0.2s ease;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;

  &:focus {
    outline: none;
    border-color: ${({ hasError }) => (hasError ? '#ff0000' : '#388e3c')};
  }

  &:hover {
    background-color: #f1f1f1;
  }

  option {
    color: #333;
    background-color: #fff;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }
`;

const FormButtonContainer = styled.div`
  text-align: center;
  margin: 10px 0;
`;

const SubmitButton = styled.button`
  padding: 8px 16px;
  font-size: 14px;
  font-weight: bold;
  background: ${({ darkMode }) => (darkMode ? '#4caf50' : '#18591d')};
  color: #fff;
  border: 1px solid ${({ darkMode }) => (darkMode ? '#388e3c' : '#144d17')};
  border-radius: 4px;
  cursor: pointer;
  transition: background-color 0.2s ease;

  &:hover {
    background: ${({ darkMode }) => (darkMode ? '#388e3c' : '#218838')};
  }
`;

const CloseButton = styled.button`
  background Alone: none;
  border: none;
  font-size: 18px;
  font-weight: bold;
  color: ${({ darkMode }) => (darkMode ? '#ffffff' : '#333')};
  cursor: pointer;
  padding: 5px 10px;
  transition: color 0.2s ease;

  &:hover {
    color: ${({ darkMode }) => (darkMode ? '#ff5555' : '#cc0000')};
  }
`;